import React, { useState } from 'react';
import { Package, Download, Loader2, CheckCircle2, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { downloadEstatePlanningKit } from '../utils/estatePlanningKit';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { hp } from '../config/homepageStrings';

const KIT_URL = 'https://zakat.international/life';
const buildEmailMeMailto = () => {
  const subj = encodeURIComponent('Download your Islamic Estate Planning Kit');
  const body = encodeURIComponent(
    `Assalamu alaykum,\n\n` +
    `Here is a free, printable Islamic Estate Planning Kit — eleven templates in one ZIP covering Zakat, Family Budget, Sadaqah, Waqf, Faraid Inheritance worksheet, Wasiyyah (Will), End-of-Life Checklist, Estate Readiness and Succession Planning, plus a cover letter explaining the recommended order.\n\n` +
    `Open this link on a desktop/laptop and click "Download the Kit (.zip)":\n${KIT_URL}\n\n` +
    `Everything is generated on your own device — nothing uploaded to any server.\n\n` +
    `— Amanah by Zakat.International (free, scholarly Islamic estate planning)`
  );
  return `mailto:?subject=${subj}&body=${body}`;
};

/**
 * One-click download for the Islamic Estate Planning Kit ZIP.
 *
 * Shows a friendly progress bar while the 10 files are built + packed
 * (takes 6-10s end-to-end on a typical laptop). All work is client-side.
 */
export const KitDownloadButton = ({
  variant = 'primary',  // 'primary' (large, gold) | 'inline' (compact, emerald)
  testid = 'kit-download',
  className = '',
}) => {
  const [busy, setBusy] = useState(false);
  const { selectedLanguage } = useZakatGlobal();
  const lang = selectedLanguage?.code || 'en';
  const [step, setStep] = useState(null);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  // Error state persists after `busy` flips back to false so users actually
  // see the failure — before Feb 2026 the message was stored in `step`, but
  // `step` was only rendered while busy, so failures were silent.
  const [error, setError] = useState(null);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    setDone(false);
    setError(null);
    try {
      await downloadEstatePlanningKit((label, frac) => {
        setStep(label);
        setProgress(frac);
      });
      setDone(true);
      setTimeout(() => { setDone(false); setStep(null); setProgress(0); }, 4000);
    } catch (e) {
      setError('Sorry, the kit failed to build — please try again.');
      setTimeout(() => setError(null), 6000);
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'inline') {
    return (
      <Button
        data-testid={testid}
        onClick={handleClick}
        disabled={busy}
        className={`bg-[#EDF5F1] hover:bg-[#EDF5F1] text-[#0D4733] ${className}`}
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : done ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Package className="w-4 h-4 mr-2" />}
        {busy ? (step || 'Building…') : done ? 'Downloaded!' : error ? error : 'Download Estate Planning Kit'}
      </Button>
    );
  }

  // primary variant — full-bleed card with progress
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#EDF5F1] via-[#EDF5F1] to-[#EDF5F1] p-6 sm:p-8 text-[#0D1520] ${className}`}>
      <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_top_right,_white,_transparent_60%)]" />
      <div className="relative">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-gold-500 text-[#0D1520] inline-flex items-center justify-center shrink-0">
            <Package className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="text-xl sm:text-2xl font-heading font-bold text-gold-200 mb-1">
              {hp(lang, 'kitTitle')}
            </h3>
            <p className="text-sm text-[#0D1520] leading-relaxed">
              One ZIP, printable Word + CSV templates covering the full Amanah workflow — <strong className="text-gold-100">Zakat</strong>, Budget, Sadaqah, Waqf, <strong className="text-gold-100">Inheritance (Faraid)</strong>, <strong className="text-gold-100">Will (Wasiyyah)</strong>, End-of-Life Checklist, Estate Readiness and Succession Planning workbook.
              Print it, fill it by hand, take it to your scholar and lawyer. Generated on your device — never uploaded.
            </p>
          </div>
        </div>

        <Button
          data-testid={testid}
          onClick={handleClick}
          disabled={busy}
          size="lg"
          className="mt-5 bg-gold-500 hover:bg-gold-400 text-[#0D1520] font-semibold w-full sm:w-auto"
        >
          {busy ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building &mdash; {step}</>
          ) : done ? (
            <><CheckCircle2 className="w-4 h-4 mr-2" /> Downloaded — check your downloads folder</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> {hp(lang, 'kitDownload')}</>
          )}
        </Button>

        <a
          href={buildEmailMeMailto()}
          data-testid={`${testid}-email-me`}
          className="ml-0 sm:ml-3 mt-3 sm:mt-5 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-sm font-semibold border border-gold-500/40 text-gold-200 hover:bg-[#EFE7D8] transition-colors"
        >
          <Mail className="w-4 h-4" /> {hp(lang, 'kitEmail')}
        </a>

        {busy && (
          <div className="mt-3 h-1.5 bg-[#EDF5F1] rounded-full overflow-hidden">
            <div className="h-full bg-gold-500 transition-all"
              style={{ width: `${Math.round(progress * 100)}%` }}
              data-testid="kit-progress-bar"
            />
          </div>
        )}

        {error && (
          <div
            data-testid="kit-error"
            role="alert"
            className="mt-3 rounded-lg bg-rose-950/40 border border-rose-400/40 text-rose-100 text-sm px-3 py-2"
          >
            {error}
          </div>
        )}

        <p className="mt-3 text-[11px] text-[#8B8172] leading-relaxed">
          Includes a cover &ldquo;Start here&rdquo; letter explaining the recommended order. Format: .docx + .csv inside a single .zip. ~250 KB. Works offline once downloaded.
        </p>
      </div>
    </div>
  );
};

export default KitDownloadButton;
